import * as React from 'react';
import classNames from 'classnames';
import { Link } from 'gatsby';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import * as styles from './Pagination.module.scss';

const DEFAULT_PAGES_DISPLAYED = 4;

export interface PaginationProps {
    totalPages: number;
    currentPage: number;
    toBuilder: (page: number) => string;
    maxPagesDisplayed?: number;
}

interface PaginationItem {
    to?: string;
    label: React.ReactNode;
    key: string;
    isActive: boolean;
}

export default function Pagination({
    totalPages,
    currentPage,
    toBuilder,
    maxPagesDisplayed = DEFAULT_PAGES_DISPLAYED,
}: PaginationProps) {
    const items = React.useMemo<ReadonlyArray<PaginationItem>>(() => {
        const middlePosition = Math.ceil(maxPagesDisplayed / 2);
        const hasPrevious = currentPage > 1;
        const hasNext = currentPage < totalPages;
        const maxPage = Math.min(
            totalPages,
            currentPage + (maxPagesDisplayed - middlePosition),
        );
        const minPage = Math.max(1, maxPage + 1 - maxPagesDisplayed);
        const hasMore = maxPage < totalPages;

        const elements: PaginationItem[] = [];

        if (hasPrevious) {
            elements.push({
                to: toBuilder(currentPage - 1),
                key: 'prev',
                isActive: false,
                label: (
                    <>
                        <FaChevronLeft /> Prev
                    </>
                ),
            });
        }

        for (let i = minPage; i <= maxPage; i += 1) {
            elements.push({
                key: `page${i}`,
                label: i.toString(10),
                isActive: i === currentPage,
                to: toBuilder(i),
            });
        }

        if (hasMore) {
            elements.push({
                key: 'more',
                label: '...',
                isActive: false,
            });
        }

        if (hasNext) {
            elements.push({
                to: toBuilder(currentPage + 1),
                key: 'next',
                label: (
                    <>
                        Next <FaChevronRight />
                    </>
                ),
                isActive: false,
            });
        }

        return elements;
    }, [totalPages, currentPage, toBuilder, maxPagesDisplayed]);

    return (
        <ul className={styles.root}>
            {items.map(({ to, label, isActive, key }) => {
                const content = to ? <Link to={to}>{label}</Link> : label;

                return (
                    <li
                        key={key}
                        className={classNames(styles.item, {
                            [styles.active]: isActive,
                        })}
                    >
                        {content}
                    </li>
                );
            })}
        </ul>
    );
}
